package com.karawek.core.util

/**
 * Utilities for phone number formatting and conversion.
 *
 * Storage format: E.164 (e.g., "+15551234567")
 * Display format: (000) 000-0000 (e.g., "(555) 123-4567")
 */
object PhoneNumberFormatter {

    /**
     * Converts a phone number to E.164 format for storage.
     * Assumes US phone numbers (+1 country code).
     *
     * @param phoneNumber The phone number in any format
     * @return The phone number in E.164 format (e.g., "+15551234567"), or null if invalid
     */
    fun toE164(phoneNumber: String): String? {
        val digitsOnly = phoneNumber.filter { it.isDigit() }

        // Must be exactly 10 digits for US numbers
        if (digitsOnly.length != 10) {
            return null
        }

        return "+1$digitsOnly"
    }

    /**
     * Converts an E.164 phone number to display format (000) 000-0000.
     *
     * @param e164PhoneNumber The phone number in E.164 format (e.g., "+15551234567")
     * @return The formatted phone number (e.g., "(555) 123-4567"), or the original if invalid
     */
    fun toDisplayFormat(e164PhoneNumber: String): String {
        // Remove + and country code (assuming +1)
        val digitsOnly = e164PhoneNumber.filter { it.isDigit() }

        // Get last 10 digits (US phone number)
        if (digitsOnly.length < 10) {
            return e164PhoneNumber
        }

        val lastTen = digitsOnly.takeLast(10)
        val area = lastTen.substring(0, 3)
        val prefix = lastTen.substring(3, 6)
        val line = lastTen.substring(6, 10)

        return "($area) $prefix-$line"
    }

    /**
     * Formats a phone number string according to display format as user types.
     * Used for real-time formatting in text fields.
     *
     * @param digitsOnly The raw digits (without formatting)
     * @return The formatted phone number string
     */
    fun formatAsTyping(digitsOnly: String): String {
        if (digitsOnly.isEmpty()) return ""

        val result = StringBuilder()
        val format = "(000) 000-0000"
        var digitIndex = 0

        for (char in format) {
            if (digitIndex >= digitsOnly.length) break

            if (char == '0') {
                result.append(digitsOnly[digitIndex])
                digitIndex++
            } else {
                result.append(char)
            }
        }

        return result.toString()
    }

    /**
     * Validates if a phone number has the required 10 digits for US format.
     *
     * @param phoneNumber The phone number in any format
     * @return true if the phone number has exactly 10 digits
     */
    fun isValid(phoneNumber: String): Boolean {
        val digitsOnly = phoneNumber.filter { it.isDigit() }
        return digitsOnly.length == 10
    }
}
